import { useTranslation } from 'react-i18next';

import type { ApiError } from '@/api/client';
import { apiErrorCode } from '@/lib/apiErrorCode';
import { humanizeError } from '@/lib/errors';
import { TEMPLATE_CODE_HINT_KEY } from '@/lib/notificationTemplates';

/**
 * Отказ сервера. Причину различаем ТОЛЬКО по `code` (§0 договора): текст
 * `message` для человека и может меняться.
 *
 * ⚠️ 403 ПРИХОДИТ БЕЗ `code` И НЕ В ОБЩЕМ КОНВЕРТЕ — это общая охрана ролей
 * (`{"error": "access denied: …"}`), а не ручка шаблонов. Поэтому у него своя
 * подсказка по коду HTTP, а не по `code`.
 */
export function ApiErrorText({ error }: { error: unknown }) {
  const { t } = useTranslation();
  if (!error) return null;
  const code = apiErrorCode(error);
  const status = (error as unknown as ApiError | null)?.status ?? 0;
  const hint =
    code && TEMPLATE_CODE_HINT_KEY[code]
      ? t(TEMPLATE_CODE_HINT_KEY[code])
      : status === 403
        ? t('adminNotificationTemplates.error.forbidden')
        : humanizeError(error);
  const tech = [status ? `HTTP ${status}` : '', code ?? ''].filter(Boolean).join(' · ');
  return (
    <div
      data-templates-error
      role="alert"
      className="rounded-xl bg-error-container px-3 py-2 text-sm text-on-error-container"
    >
      <div>{hint}</div>
      {tech ? <div className="mt-0.5 font-mono text-xs opacity-80">{tech}</div> : null}
    </div>
  );
}

export function OkLine({ text }: { text: string }) {
  if (!text) return null;
  return (
    <div data-templates-ok className="rounded-xl bg-success-container px-3 py-2 text-sm text-on-success-container">
      {text}
    </div>
  );
}
